import {
  getSupplierResourceListDataBySupplierId,
  getSupplierResourceListDataWithUseForRegistrationBySupplierId,
} from '../supplierResources/mockData'
import { getTaskListDataBySupplierId } from '../tasks/mockData'
import { getCallListDataBySupplierId } from '../calls/mockData'
import { getEmailListDataBySupplierId } from '../emails/mockData'
import { getMeetingListDataBySupplierId } from '../meetings/mockData'
import { getSupplierDocumentListDataBySupplierId } from '../supplierDocuments/mockData'
import { getSupplierNoteListDataBySupplierId } from '../supplierNotes/mockData'
import { getSupplierLastSelectedStateDataBySupplierId } from '../supplierLastSelectedStates/mockData'
import { getTsdSupplierListDataBySupplierId } from '../tsdSuppliers/mockData'
import { getListDataFunctionFactory } from '../../modules/getListDataFunctionFactory'
import {
  getProductGroupListData,
  _getPRGDataById,
} from '../productGroups/mockData'
import { getProductSubcategoryListData } from '../productSubcategories/mockData'
import { getStateDataById } from '../selectLists/states/mockData'
import {
  getSupplierContactListData,
  getSupplierContactListDataBySupplierId,
  getSupplierContactListDataWithUseForRegistrationBySupplierId,
  _getSCTDataById,
} from '~/api/msw/api/supplierContacts/mockData'
import { getSupplierProductListData } from '~/api/msw/api/supplierProducts/mockData'
import type { ISelectItem, IEntitySelectItem, TEntityId } from '~/types/models'
import type { ISupplier, ISupplierOverview } from '@/types/supplier/models'
import type { ISupplierProduct } from '~/types/supplierProduct/models'
const supplierListData = undefined as unknown as ISupplier[]

export const getSupplierListData = getListDataFunctionFactory<ISupplier[]>({
  listData: supplierListData,
  storageKey: 'suppliers',
  getStartingListData: getStartingSupplierListData,
})

export function _getSDataById(id: TEntityId, list: ISupplier[]): ISupplier {
  if (!list) {
    throw new Error(`_getSDataById called with undefined/null list`)
  }
  const supplier = list.find((s: ISupplier) => s.id === id)
  if (!supplier) {
    throw new Error(`Can't find supplier '${id}'`)
  }
  return supplier
}

export function _getSSelectItemDataById(
  id: TEntityId,
  list: ISupplier[]
): ISelectItem {
  if (!list) {
    throw new Error(`_getSSelectItemDataById called with undefined/null list`)
  }
  const supplier = _getSDataById(id, list)
  return { id: supplier.id, label: supplier.descr }
}

export async function getSupplierData(
  id: TEntityId
): Promise<ISupplier | undefined> {
  const list = await getSupplierListData()
  return _getSDataById(id, list)
}

export async function getSupplierSelectData(
  id: TEntityId
): Promise<ISelectItem> {
  const list = await getSupplierListData()
  return _getSSelectItemDataById(id, list)
}

export async function getSupplierOverviewData(
  id: string
): Promise<ISupplierOverview | undefined> {
  const supplierData = await getSupplierData(id)
  if (!supplierData) {
    return undefined
  }
  const overviewData = {
    supplier: supplierData as ISupplier,
    supplierProducts: (await getSupplierProductsListData(id)) || [],
    supplierContacts: (await getSupplierContactListDataBySupplierId(id)) || [],
    supplierResources:
      (await getSupplierResourceListDataBySupplierId(id)) || [],
    tsdSuppliers: (await getTsdSupplierListDataBySupplierId(id)) || [],
    supplierDocuments:
      (await getSupplierDocumentListDataBySupplierId(id)) || [],
    supplierNotes: (await getSupplierNoteListDataBySupplierId(id)) || [],
    opportunities: [],
    projects: [],
    orderServiceOrders: [],
    serviceRequests: [],
    cases: [],
    contracts: [],
    tasks: (await getTaskListDataBySupplierId(id)) || [],
    calls: (await getCallListDataBySupplierId(id)) || [],
    emails: (await getEmailListDataBySupplierId(id)) || [],
    meetings: (await getMeetingListDataBySupplierId(id)) || [],
    // Every other silo's overview payload carries this, and ISupplierOverview
    // has always declared it — the omission here was a pre-existing TS2741 that
    // only became visible as behaviour once the payload became the single
    // seeding path for the store: without it a reload left the store empty and
    // the next tab change created a SECOND row.
    lastSelectedState: await getSupplierLastSelectedStateDataBySupplierId(id),
  }
  console.debug(overviewData)
  return overviewData
}

export async function getSupplierProductsListData(id: TEntityId) {
  const list = await getSupplierProductListData()
  if (!list) {
    throw new Error(
      `getSupplierProductsListData couldn't get supplierProduct list`
    )
  }
  const supplierProductsListData: ISupplierProduct[] = list
    .filter((sp: ISupplierProduct) => sp.supplier.id === id)
    .sort((a: ISupplierProduct, b: ISupplierProduct) => a.seqNum - b.seqNum)
  return supplierProductsListData
}

export async function getProductGroupsForSupplierListData(id: TEntityId) {
  const list = await getSupplierProductListData()
  if (!list) {
    throw new Error(
      `getProductGroupsForSupplierListData couldn't get supplierProduct list`
    )
  }
  const dedupedGroups: IEntitySelectItem[] = list
    .filter((sp: ISupplierProduct) => sp.supplier.id === id)
    .map((sp2: ISupplierProduct) => sp2.product.productGroup)
    .reduce((accum: IEntitySelectItem[], pg: IEntitySelectItem) => {
      if (!accum.find((e) => e.id === pg.id)) {
        accum.push(pg)
      }
      return accum
    }, [])
  // Enrich each group with its parent chain (subcategory → category) so the
  // supplier-first input cascade can derive Category/Subcategory/Group from
  // this single list. (Omit parent if a lookup misses.)
  const allGroups = await getProductGroupListData()
  const allSubcategories = await getProductSubcategoryListData()
  const productGroupSelectItemsForSupplierListData: IEntitySelectItem[] =
    dedupedGroups.map((pg) => {
      const fullGroup = allGroups.find((g) => g.id === pg.id)
      const subcategory = fullGroup?.productSubcategory
      const fullSubcategory = subcategory
        ? allSubcategories.find((s) => s.id === subcategory.id)
        : undefined
      const category = fullSubcategory?.productCategory
      return {
        id: pg.id,
        label: pg.label,
        ...(subcategory && {
          parent: {
            id: subcategory.id,
            label: subcategory.label,
            ...(category && {
              parent: { id: category.id, label: category.label },
            }),
          },
        }),
      }
    })
  return productGroupSelectItemsForSupplierListData
}

export async function getSupplierProductDataForSupplierAndProduct(
  supplierId: string,
  productId: string,
  wellFormedSupplierProductsOnly: boolean = false
) {
  const list = await getSupplierProductListData()
  if (!list) {
    throw new Error(
      `getSupplierProductDataForSupplierAndProduct couldn't get supplierProduct list`
    )
  }
  const supplierProduct: ISupplierProduct | undefined = list.find(
    (sp: ISupplierProduct) =>
      sp.supplier.id === supplierId &&
      sp.product.id === productId &&
      (!wellFormedSupplierProductsOnly ||
        (wellFormedSupplierProductsOnly && sp.numItems && sp.numItems > 0))
  )
  return supplierProduct
}

export async function getSupplierOpportunityRegistrationEmailAddressListDataBySupplier(
  id: TEntityId
): Promise<string[]> {
  console.group()
  const supplierContactsWithUseForRegistration =
    await getSupplierContactListDataWithUseForRegistrationBySupplierId(id)
  console.debug(
    'supplierContactsWithUseForRegistration:',
    supplierContactsWithUseForRegistration
  )
  const supplierResourcesWithUseForRegistration =
    await getSupplierResourceListDataWithUseForRegistrationBySupplierId(id)
  console.debug(
    'supplierResourcesWithUseForRegistration:',
    supplierResourcesWithUseForRegistration
  )
  // see https://medium.com/@lukasbach/typescript-properly-filtering-arrays-for-non-null-values-c8c1b5563c6a
  const isNotUndefined = <T>(value: T | undefined): value is T =>
    value !== undefined

  const theContactsEmailsArray = supplierContactsWithUseForRegistration
    .map((sc) => sc.email)
    .filter(isNotUndefined)
  console.debug('theContactsEmailsArray:', theContactsEmailsArray)
  const theResourcesEmailsArray = supplierResourcesWithUseForRegistration
    .map((sr) => sr.email)
    .filter(isNotUndefined)
  console.debug('theResourcesEmailsArray', theResourcesEmailsArray)
  const theEmailsArray = theContactsEmailsArray.concat(theResourcesEmailsArray)
  console.debug('theEmailsArray:', theEmailsArray)
  console.groupEnd()
  return theEmailsArray
}

export async function getStartingSupplierListData(): Promise<ISupplier[]> {
  const sctList = await getSupplierContactListData()
  const prgList = await getProductGroupListData()
  const massachusetts = await getStateDataById(1433)
  const illinois = await getStateDataById(1425)
  const california = await getStateDataById(1416)
  return [
    {
      id: 'XXS0001',
      hrid: 'S0001',
      descr: 'WXTG',
      mainPhone: '510-333-4444',
      primaryContact: _getSCTDataById('XXSCT0001', sctList),
      address: {
        country: {
          id: 233,
          name: 'United States',
          iso2: 'US',
          iso3: 'USA',
          numericCode: '840',
        },
        addressLine1: '234 Somewhere Street',
        city: 'Boston',
        state: massachusetts,
        zip: '04359',
      },
      mainProductGroup: _getPRGDataById('XXPRG0003', prgList),
      numSupplierProducts: 2,
      numOpportunities: 490,
      numOrders: 425,
      numServices: 425,
      lastActivityDate: '2022-03-05',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXS0002',
      hrid: 'S0002',
      descr: 'Cogent',
      mainPhone: '510-543-4321',
      primaryContact: _getSCTDataById('XXSCT0002', sctList),
      address: {
        country: {
          id: 233,
          name: 'United States',
          iso2: 'US',
          iso3: 'USA',
          numericCode: '840',
        },
        addressLine1: '456 Somewhere Street',
        city: 'Boston',
        state: massachusetts,
        zip: '04359',
      },
      mainProductGroup: _getPRGDataById('XXPRG0002', prgList),
      numSupplierProducts: 2,
      numOpportunities: 10,
      numOrders: 7,
      numServices: 62,
      lastActivityDate: '2021-12-01',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXS0003',
      hrid: 'S0003',
      descr: 'Comcast',
      mainPhone: '510-123-2345',
      primaryContact: _getSCTDataById('XXSCT0003', sctList),
      address: {
        country: {
          id: 233,
          name: 'United States',
          iso2: 'US',
          iso3: 'USA',
          numericCode: '840',
        },
        addressLine1: '567 Somewhere Street',
        city: 'Boston',
        state: massachusetts,
        zip: '04359',
      },
      mainProductGroup: _getPRGDataById('XXPRG0002', prgList),
      numSupplierProducts: 2,
      numOpportunities: 39,
      numOrders: 26,
      numServices: 120,
      lastActivityDate: '2022-01-03',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXS0004',
      hrid: 'S0004',
      descr: 'Nitel',
      mainPhone: '530-876-8765',
      primaryContact: _getSCTDataById('XXSCT0004', sctList),
      address: {
        country: {
          id: 233,
          name: 'United States',
          iso2: 'US',
          iso3: 'USA',
          numericCode: '840',
        },
        addressLine1: '888 Bingo Lane',
        city: 'Boston',
        state: massachusetts,
        zip: '04359',
      },
      mainProductGroup: _getPRGDataById('XXPRG0002', prgList),
      numSupplierProducts: 1,
      numOpportunities: 34,
      numOrders: 14,
      numServices: 44,
      lastActivityDate: '2022-02-02',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXS0005',
      hrid: 'S0005',
      descr: 'RingCentral',
      mainPhone: '310-839-8799',
      primaryContact: _getSCTDataById('XXSCT0005', sctList),
      address: {
        country: {
          id: 233,
          name: 'United States',
          iso2: 'US',
          iso3: 'USA',
          numericCode: '840',
        },
        addressLine1: '678 Somewhere Street',
        city: 'Boston',
        state: massachusetts,
        zip: '04359',
      },
      mainProductGroup: _getPRGDataById('XXPRG0006', prgList),
      numSupplierProducts: 3,
      numOpportunities: 123,
      numOrders: 65,
      numServices: 650,
      lastActivityDate: '2022-02-08',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXS0006',
      hrid: 'S0006',
      descr: 'Genesys',
      mainPhone: '838-529-1000',
      primaryContact: _getSCTDataById('XXSCT0006', sctList),
      address: {
        country: {
          id: 233,
          name: 'United States',
          iso2: 'US',
          iso3: 'USA',
          numericCode: '840',
        },
        addressLine1: '999 There Street',
        city: 'Boston',
        state: massachusetts,
        zip: '04359',
      },
      mainProductGroup: _getPRGDataById('XXPRG0010', prgList),
      numSupplierProducts: 1,
      numOpportunities: 60,
      numOrders: 50,
      numServices: 48,
      lastActivityDate: '2022-02-06',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXS0007',
      hrid: 'S0007',
      descr: '8X8',
      mainPhone: '727-800-9000',
      primaryContact: _getSCTDataById('XXSCT0007', sctList),
      address: {
        country: {
          id: 233,
          name: 'United States',
          iso2: 'US',
          iso3: 'USA',
          numericCode: '840',
        },
        addressLine1: '876 There Street',
        city: 'Boston',
        state: massachusetts,
        zip: '04359',
      },
      mainProductGroup: _getPRGDataById('XXPRG0010', prgList),
      numSupplierProducts: 1,
      numOpportunities: 39,
      numOrders: 10,
      numServices: 100,
      lastActivityDate: '2022-02-01',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXS0008',
      hrid: 'S0008',
      descr: 'Five9',
      mainPhone: '213-122-8500',
      primaryContact: _getSCTDataById('XXSCT0008', sctList),
      address: {
        country: {
          id: 233,
          name: 'United States',
          iso2: 'US',
          iso3: 'USA',
          numericCode: '840',
        },
        addressLine1: '111 Big Street',
        city: 'Boston',
        state: massachusetts,
        zip: '04359',
      },
      mainProductGroup: _getPRGDataById('XXPRG0010', prgList),
      numSupplierProducts: 1,
      numOpportunities: 10,
      numOrders: 5,
      numServices: 5,
      lastActivityDate: '2021-11-29',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXS0009',
      hrid: 'S0009',
      descr: 'PacTel',
      mainPhone: '310-833-8795',
      primaryContact: _getSCTDataById('XXSCT0009', sctList),
      address: {
        country: {
          id: 233,
          name: 'United States',
          iso2: 'US',
          iso3: 'USA',
          numericCode: '840',
        },
        addressLine1: '143 Somewhere Street',
        city: 'Boston',
        state: massachusetts,
        zip: '04359',
      },
      mainProductGroup: _getPRGDataById('XXPRG0005', prgList),
      numSupplierProducts: 1,
      numOpportunities: 30,
      numOrders: 10,
      numServices: 10,
      lastActivityDate: '2022-02-02',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXS0010',
      hrid: 'S0010',
      descr: 'Coeo',
      mainPhone: '844-300-2636',
      primaryContact: undefined,
      address: {
        country: {
          id: 233,
          name: 'United States',
          iso2: 'US',
          iso3: 'USA',
          numericCode: '840',
        },
        addressLine1: '1901 Butterfield Road',
        addressLine2: 'Suite 150',
        city: 'Downers Grove',
        state: illinois,
        zip: '60515',
      },
      mainProductGroup: _getPRGDataById('XXPRG0009', prgList),
      // XXSP0010 "Coeo SDWAN (Kit)" and XXSP0023 "Coeo SDWAN Device (Set)".
      numSupplierProducts: 2,
      numOpportunities: 26,
      numOrders: 22,
      numServices: 22,
      lastActivityDate: '2023-02-05',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      // The supplier of production's Zscaler DLP kits. Address is a placeholder.
      id: 'XXS0011',
      hrid: 'S0011',
      descr: 'AppDirect',
      mainPhone: '415-555-0142',
      primaryContact: undefined,
      address: {
        country: {
          id: 233,
          name: 'United States',
          iso2: 'US',
          iso3: 'USA',
          numericCode: '840',
        },
        addressLine1: '100 Market Street',
        city: 'San Francisco',
        state: california,
        zip: '94105',
      },
      mainProductGroup: _getPRGDataById('XXPRG0001', prgList),
      // XXSP0028-XXSP0031, the Zscaler DLP - 3 Year kit and what it holds.
      numSupplierProducts: 4,
      numOpportunities: 1,
      numOrders: 0,
      numServices: 0,
      lastActivityDate: '2026-10-07',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
  ]
}
